/* Admin: Bookings & sales inquiries inbox */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Inbox, Mail, Phone, Calendar, Check, X as XIcon, Shield, Route, Tag } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INSURANCE_LABELS = {
  basic: "Standard (15.000 kr. selvrisiko)",
  extra: "Reduceret selvrisiko (5.000 kr.)",
  full: "Nul selvrisiko (0 kr.)",
};

function dkk(n) {
  if (n == null || n === "") return "—";
  return new Intl.NumberFormat("da-DK", { maximumFractionDigits: 0 }).format(Math.round(Number(n))) + " kr.";
}

function authHdr() {
  const t = localStorage.getItem("mahasa_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const STATUS_LABELS = { pending: "Afventer", approved: "Godkendt", rejected: "Afvist", completed: "Afsluttet" };
const TYPE_LABELS = { rental: "Udlejning", sale_inquiry: "Salgs-forespørgsel", sell_inquiry: "Vil sælge sin bil", general: "Generel kontakt" };

// Sell inquiries have their own status vocabulary; translate booking-status back
// so the shared UI can drive both types with the same buttons.
const BOOKING_TO_SELL_STATUS = {
  pending: "new",
  approved: "contacted",
  rejected: "declined",
  completed: "purchased",
};

function endpointFor(b, id) {
  return b.source === "sell_inquiry" ? `${API}/sell-inquiries/${id}` : `${API}/bookings/${id}`;
}

function nkFmt(n) {
  if (n == null || n === "") return null;
  return new Intl.NumberFormat("da-DK", { maximumFractionDigits: 0 }).format(Math.round(Number(n)));
}

export default function Bookings() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [confirmDel, setConfirmDel] = useState(null);  // booking id awaiting 2nd-click

  async function load() {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/bookings${statusFilter ? `?status_filter=${statusFilter}` : ""}`, { headers: authHdr() });
      setItems(r.data);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [statusFilter]);

  async function setStatus(id, status) {
    const b = items.find((x) => x.id === id);
    const url = endpointFor(b || {}, id);
    const payloadStatus = b?.source === "sell_inquiry" ? BOOKING_TO_SELL_STATUS[status] : status;
    try {
      await axios.patch(url, { status: payloadStatus }, { headers: authHdr() });
      toast.success("Opdateret");
      load();
    } catch { toast.error("Kunne ikke opdatere"); }
  }

  async function del(id) {
    // Two-step confirm: first click arms the button, second click deletes
    if (confirmDel !== id) {
      setConfirmDel(id);
      setTimeout(() => setConfirmDel((c) => (c === id ? null : c)), 4000);  // auto-cancel after 4s
      return;
    }
    const b = items.find((x) => x.id === id);
    const url = endpointFor(b || {}, id);
    try {
      await axios.delete(url, { headers: authHdr() });
      toast.success("Slettet");
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error("Kunne ikke slette: " + (e.response?.data?.detail || e.message));
    }
  }

  const counts = useMemo(() => ({
    pending: items.filter((i) => i.status === "pending").length,
    approved: items.filter((i) => i.status === "approved").length,
  }), [items]);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2"><Inbox size={22} className="text-amber-500"/>Forespørgsler</h1>
        <p className="text-zinc-400 text-sm mt-1">Booking- og salgs-forespørgsler fra mahasa.dk</p>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="bookings-tabs">
        {["pending", "approved", "rejected", "completed", ""].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setStatusFilter(s)}
            className={`px-4 py-1.5 rounded-full text-sm border transition ${
              statusFilter === s ? "bg-amber-500 text-zinc-900 border-amber-500" : "bg-zinc-900 text-zinc-300 border-zinc-800 hover:border-zinc-600"
            }`}
            data-testid={`booking-tab-${s || "all"}`}
          >
            {s === "" ? "Alle" : STATUS_LABELS[s]}
          </button>
        ))}
      </div>

      {loading && <div className="text-zinc-500 text-sm">Indlæser…</div>}

      {!loading && items.length === 0 && (
        <div className="text-center py-16 bg-zinc-900/50 border border-zinc-800 rounded-xl text-zinc-500" data-testid="no-bookings">
          Ingen forespørgsler her endnu.
        </div>
      )}

      <div className="space-y-3">
        {items.map((b) => (
          <div key={b.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 sm:p-5" data-testid={`booking-${b.id}`}>
            <div className="flex flex-wrap gap-3 items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs uppercase tracking-wider text-amber-500">{TYPE_LABELS[b.type] || b.type}</span>
                  {b.car_label && <span className="text-zinc-400 text-sm">· {b.car_label}</span>}
                  {b.car_plate && <span className="font-mono text-amber-500/70 text-xs">({b.car_plate})</span>}
                </div>
                <div className="text-lg font-semibold text-zinc-100">{b.name}</div>
                <div className="flex flex-wrap gap-3 text-xs text-zinc-400 mt-1">
                  <a href={`mailto:${b.email}`} className="inline-flex items-center gap-1 hover:text-zinc-200"><Mail size={12}/>{b.email}</a>
                  <a href={`tel:${b.phone}`} className="inline-flex items-center gap-1 hover:text-zinc-200"><Phone size={12}/>{b.phone}</a>
                  {b.start_date && (
                    <span className="inline-flex items-center gap-1"><Calendar size={12}/>{b.start_date} → {b.end_date || "?"}</span>
                  )}
                </div>
              </div>
              <span className={`text-[10px] px-2.5 py-1 rounded-full font-semibold uppercase tracking-wider ${statusClass(b.status)}`}>
                {STATUS_LABELS[b.status] || b.status}
              </span>
            </div>
            {b.message && <p className="text-zinc-300 text-sm bg-zinc-800/50 rounded-lg p-3 mb-3 whitespace-pre-line">{b.message}</p>}

            {/* Sælg-din-bil detaljer — kundens bil */}
            {b.type === "sell_inquiry" && (b.sell_km != null || b.sell_asking_price != null || b.sell_condition || b.sell_fuel_type || b.sell_transmission) && (
              <div className="bg-[#10161A] border-l-2 border-emerald-500 rounded-r-lg p-3 mb-3" data-testid={`sell-details-${b.id}`}>
                <div className="text-[10px] uppercase tracking-wider text-emerald-400 mb-2 font-bold">Bilens tilstand</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                  {b.sell_km != null && (
                    <div>
                      <div className="text-zinc-500 text-[10px] uppercase tracking-wider mb-0.5">Kilometerstand</div>
                      <div className="text-zinc-200 font-semibold">{nkFmt(b.sell_km)} km</div>
                    </div>
                  )}
                  {b.sell_condition && (
                    <div>
                      <div className="text-zinc-500 text-[10px] uppercase tracking-wider mb-0.5">Stand</div>
                      <div className="text-zinc-200 font-semibold capitalize">{b.sell_condition}</div>
                    </div>
                  )}
                  {b.sell_fuel_type && (
                    <div>
                      <div className="text-zinc-500 text-[10px] uppercase tracking-wider mb-0.5">Brændstof</div>
                      <div className="text-zinc-200 font-semibold">{b.sell_fuel_type}</div>
                    </div>
                  )}
                  {b.sell_transmission && (
                    <div>
                      <div className="text-zinc-500 text-[10px] uppercase tracking-wider mb-0.5">Gearkasse</div>
                      <div className="text-zinc-200 font-semibold">{b.sell_transmission}</div>
                    </div>
                  )}
                </div>
                {b.sell_asking_price != null && (
                  <div className="mt-3 pt-3 border-t border-emerald-500/20 flex items-baseline gap-1.5">
                    <Tag size={11} className="text-emerald-400 self-center"/>
                    <span className="text-zinc-500 uppercase tracking-wider text-[10px]">Ønsket pris:</span>
                    <span className="font-display font-bold text-lg text-emerald-400 tabular-nums">{dkk(b.sell_asking_price)}</span>
                  </div>
                )}
              </div>
            )}

            {/* Kundens valg på hjemmesiden — kun for udlejning */}
            {b.type === "rental" && (b.binding_months || b.km_package || b.insurance_package || b.monthly_total) && (
              <div className="bg-[#1A1410] border-l-2 border-[#C8A661] rounded-r-lg p-3 mb-3" data-testid={`booking-choices-${b.id}`}>
                <div className="text-[10px] uppercase tracking-wider text-[#C8A661] mb-2 font-bold">Kundens valg</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                  {b.binding_months != null && (
                    <div>
                      <div className="text-zinc-500 text-[10px] uppercase tracking-wider mb-0.5 flex items-center gap-1"><Calendar size={10}/>Binding</div>
                      <div className="text-zinc-200 font-semibold">{b.binding_months} {b.binding_months === 1 ? "måned" : "måneder"}</div>
                    </div>
                  )}
                  {b.km_package != null && (
                    <div>
                      <div className="text-zinc-500 text-[10px] uppercase tracking-wider mb-0.5 flex items-center gap-1"><Route size={10}/>Km pr. md</div>
                      <div className="text-zinc-200 font-semibold">{Number(b.km_package).toLocaleString("da-DK")} km{b.km_addon ? <span className="text-zinc-500 font-normal"> · +{dkk(b.km_addon)}/md</span> : <span className="text-emerald-400 font-normal"> · inkl.</span>}</div>
                    </div>
                  )}
                  {b.insurance_package && (
                    <div>
                      <div className="text-zinc-500 text-[10px] uppercase tracking-wider mb-0.5 flex items-center gap-1"><Shield size={10}/>Selvrisiko</div>
                      <div className="text-zinc-200 font-semibold">{INSURANCE_LABELS[b.insurance_package] || b.insurance_package}</div>
                      {b.insurance_addon ? <div className="text-zinc-500 text-[10px]">+{dkk(b.insurance_addon)}/md</div> : <div className="text-emerald-400 text-[10px]">Inkluderet</div>}
                    </div>
                  )}
                </div>
                {(b.monthly_total != null || b.period_total != null) && (
                  <div className="mt-3 pt-3 border-t border-[#C8A661]/20 flex flex-wrap items-baseline gap-x-6 gap-y-1">
                    <div className="text-xs flex items-baseline gap-1.5">
                      <Tag size={11} className="text-[#C8A661] self-center"/>
                      <span className="text-zinc-500 uppercase tracking-wider text-[10px]">Pris pr. md:</span>
                      <span className="font-display font-bold text-lg text-[#C8A661] tabular-nums">{dkk(b.monthly_total)}</span>
                    </div>
                    {b.period_total != null && b.binding_months && (
                      <div className="text-xs flex items-baseline gap-1.5">
                        <span className="text-zinc-500 uppercase tracking-wider text-[10px]">Samlet over {b.binding_months} {b.binding_months === 1 ? "md" : "mdr"}:</span>
                        <span className="font-display font-bold text-zinc-200 tabular-nums">{dkk(b.period_total)}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="text-zinc-500 mr-2">Modtaget {new Date(b.created_at).toLocaleString("da-DK")}</span>
              <div className="flex gap-1 ml-auto">
                {b.status !== "approved" && (
                  <button onClick={() => setStatus(b.id, "approved")} className="px-3 py-1 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-400 rounded-md inline-flex items-center gap-1" data-testid={`approve-${b.id}`}>
                    <Check size={12}/> Godkend
                  </button>
                )}
                {b.status !== "rejected" && (
                  <button onClick={() => setStatus(b.id, "rejected")} className="px-3 py-1 bg-zinc-700/40 hover:bg-zinc-700/60 text-zinc-300 rounded-md inline-flex items-center gap-1" data-testid={`reject-${b.id}`}>
                    <XIcon size={12}/> Afvis
                  </button>
                )}
                {b.status !== "completed" && (
                  <button onClick={() => setStatus(b.id, "completed")} className="px-3 py-1 bg-zinc-700/40 hover:bg-zinc-700/60 text-zinc-300 rounded-md" data-testid={`complete-${b.id}`}>
                    Afsluttet
                  </button>
                )}
                <button onClick={() => del(b.id)} className={`px-3 py-1 rounded-md inline-flex items-center gap-1 transition ${confirmDel === b.id ? "bg-red-500 hover:bg-red-600 text-white font-semibold" : "bg-red-500/15 hover:bg-red-500/25 text-red-400"}`} data-testid={`del-${b.id}`}>
                  {confirmDel === b.id ? "Klik for at bekræfte" : "Slet"}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function statusClass(s) {
  if (s === "pending") return "bg-amber-500/15 text-amber-400";
  if (s === "approved") return "bg-emerald-500/15 text-emerald-400";
  if (s === "rejected") return "bg-red-500/15 text-red-400";
  if (s === "completed") return "bg-zinc-700/40 text-zinc-400";
  return "bg-zinc-700/40 text-zinc-300";
}
